import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Popout, PopoutContent, PopoutDescription, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowRight, ChevronDown, ChevronRight, Edit, PlusIcon, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { DataTable } from '@/components/data-table';
import { ExpandedRowsMobile } from '@/components/expanded-row';
import SearchInput from '@/components/search-input';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export type ProjectData = components['schemas']['ListElectricalProjectQueryResultProject'];

export type OrganizationData = components['schemas']['ListOrganizationQueryResultOrganization'];

export const Route = createFileRoute('/_auth/dashboard/organizations/')({
    component: RouteComponent,
    validateSearch: z.object({
        search: z.string().optional(),
    }),
});

function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const search = Route.useSearch();
    const navigate = Route.useNavigate();
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const [selectedOrganisation, setSelectedOrganisation] = useState<OrganizationData['OrganizationId'] | null>(null);

    const [isCreateOrganizationModalOpen, setCreateOrganizationModalOpen] = useState(false);
    const [isEditOrganizationModalOpen, setEditOrganizationModalOpen] = useState(false);

    const { currentUser } = useAuthentication();

    const columns = useMemo<ColumnDef<OrganizationData>[]>(() => ([
        {
            accessorKey: 'Name',
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.name')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell
                    variant="bold"
                >
                    <Button
                        variant="ghost"
                        className="sm:hidden mr-2"
                        onClick={() => setExpandedId(prev => prev === row?.original?.OrganizationId ? null : row?.original?.OrganizationId ?? null)}
                    >
                        {expandedId === row?.original?.OrganizationId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    {row?.original?.Name || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Description',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.description')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell
                    variant="default"
                >
                    {row?.original?.Description || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'LastActivityDate',
            meta: {
                mobileHidden: true,
            },
            header: ({ column }) => (
                <DataTable.Header variant="sortReverse" column={column}>
                    {t('dashboard.projects.table.headers.updatedAt')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell
                    variant="bold"
                >
                    {(() => {
                        const dateValue = row.original.LastActivityDate;
                        if (!dateValue)
                            return '-';
                        const date = new Date(dateValue);
                        if (Number.isNaN(date.getTime()))
                            return '-';
                        return date.toLocaleDateString('nl-NL', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        });
                    })()}
                </DataTable.Cell>
            ),
        },
        ...(
            currentUser?.IsAdmin
                ? [{
                        accessorKey: 'LastActivityUser',
                        meta: {
                            mobileHidden: true,
                        },
                        header: ({ column }: { column: any }) => (
                            <DataTable.Header
                                column={column}
                            >
                                {t('dashboard.projects.table.headers.updatedBy')}
                            </DataTable.Header>
                        ),
                        cell: ({ row }: { row: { original: OrganizationData } }) => (
                            <DataTable.Cell
                                variant="default"
                            >
                                <div className="flex flex-col">
                                    <div>
                                        {row?.original?.LastActivityUser?.DisplayName}
                                    </div>
                                    <div>
                                        {row?.original?.LastActivityUser?.Email}
                                    </div>
                                </div>
                            </DataTable.Cell>
                        ),
                    }]
                : []
        ),
        {
            id: 'actions',
            header: () => <DataTable.Header />,
            meta: {
                sticky: true,
                width: 10,
            },
            cell: ({ row }) => (
                <div>
                    {currentUser?.IsAdmin && (
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                setSelectedOrganisation(row?.original.OrganizationId);
                                setEditOrganizationModalOpen(true);
                            }}
                        >
                            <Edit className="w-4 h-4" />
                            {t('dashboard.organizations.table.columns.edit-organization')}
                        </Button>
                    )}
                    <Button
                        variant="secondary"
                        size="icon"
                        className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                        onClick={() => navigate({ to: `/dashboard/organizations/${row?.original?.OrganizationId}` })}
                    >
                        <ArrowRight className="w-4 h-4" />
                        {t('dashboard.organizations.table.columns.open-organization')}
                    </Button>
                </div>

            ),
        },
    ]), [currentUser?.IsAdmin, expandedId, navigate, t]);

    const organizationsQuery = useQuery(portaalQueryOptions.organizations.list());

    const tableColumns = useMemo(() => {
        return organizationsQuery.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns;
    }, [columns, organizationsQuery.isLoading]);

    const tableData = useMemo<OrganizationData[]>(() => {
        if (organizationsQuery.isError) {
            toast.error(organizationsQuery.error?.Message);
            return [];
        }

        const searchTerm = search.search?.toLowerCase() || '';
        return organizationsQuery.data?.Organizations?.filter((org) => {
            if (!org.ElectricalOrganizationKinds?.includes('Inspection'))
                return false;
            if (!searchTerm)
                return true;

            return [
                org.Name || '-',
                org.Description || '-',
                org.LastActivityDate || '',
                org.LastActivityUser || '',
            ].some(field => field?.toString().toLowerCase().includes(searchTerm));
        }) || [];
    }, [organizationsQuery.data, organizationsQuery.error?.Message, organizationsQuery.isError, search.search]);

    return (
        <div className="flex flex-col md:flex-wrap gap-8 justify-start">
            <div className="flex justify-between">
                <div className="flex flex-col gap-1">
                    <h3 className="text-2xl font-semibold">
                        {t('dashboard.organizations.title')}
                    </h3>
                    <p className="text-md text-text-primary/70">
                        {t('dashboard.organizations.subtitle')}
                    </p>
                </div>
                <div className="flex gap-4">
                    {/* Check button */}
                    <Button
                        variant="link"
                        className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background max-sm:hidden"
                        onClick={() => setCreateOrganizationModalOpen(true)}
                    >
                        {/* TODO: add modal functionality for check */}
                        <PlusIcon className="w-5 h-5" />
                        {t('dashboard.organizations.create')}
                    </Button>
                    <CreateOrganizationModal
                        isOpen={isCreateOrganizationModalOpen}
                        setIsOpen={setCreateOrganizationModalOpen}
                    />
                    <SearchInput route={Route} />
                </div>
            </div>
            <DataTable
                columns={tableColumns}
                data={tableData}
                pageSize={10}
                expandedRow={row =>
                    expandedId && expandedId === row?.original?.OrganizationId
                        ? (
                                <ExpandedRowsMobile columns={columns} data={row.original} />
                            )
                        : null}
                cellProps={cell => ({
                    className: cell.column.id === 'actions' ? 'w-[0]' : '',
                })}
            />
            <EditOrganizationModal
                isOpen={isEditOrganizationModalOpen}
                setIsOpen={setEditOrganizationModalOpen}
                organisationId={selectedOrganisation ?? ''}
            />
        </div>
    );
}

type FormValues = components['schemas']['CreateOrganizationCommand'];

function CreateOrganizationModal({
    isOpen,
    setIsOpen,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}) {
    const createOrganizationMutation = api.useMutation(
        'post',
        '/api/v2/organizations/create',
        {
            onSuccess: () => {
                toast.success('Organisatie aangemaakt');
            },
            onError: (error) => {
                logger.error({
                    system: 'CreateOrganisation',
                    error,
                }, { apiHttpMethod: 'Post', apiUrl: '/api/v2/organizations/create' });

                toast.error('Er is een fout opgetreden');
            },
            onSettled: () => {
                queryClient.invalidateQueries(portaalQueryOptions.organizations.list());
            },
        },
    );

    const defaultValues: FormValues = {
        Name: '',
        Description: null,
        SourceKind: 'NLKVK',
        NLKvK: {
            KVKNumber: '',
        },
        Slogan: null,
        Email: null,
        ElectricalOrganizationKinds: [],
        TelephoneNumber: null,
        Website: null,
        IBAN: null,
        BIC: null,
        VatNumber: null,
        SciosRegistrationNumber: null,
        EnableElectricalProjectRecommendations: false,
        EnableElectricalProjectPlanning: false,
        EnableElectricalProjectPriceQuote: false,
    };

    const form = useAppForm({
        defaultValues,
        onSubmit: async ({ value }) => {
            await createOrganizationMutation.mutateAsync({
                body: value,
            });
            setIsOpen(false);
            form.reset();
        },
    });

    async function handleClose() {
        if (form.state.isTouched) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsOpen(false);
                form.reset();
                createOrganizationMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            form.reset();
            createOrganizationMutation.reset();
        }
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent asChild>
                <form onSubmit={onSubmit}>
                    <PopoutHeader>
                        <PopoutTitle>Organisatie aanmaken</PopoutTitle>
                        <PopoutDescription children={<></>}></PopoutDescription>
                    </PopoutHeader>
                    <Popout.Body className="flex flex-col gap-4 py-6">
                        <form.AppField
                            name="Name"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.name,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Naam" />
                                    <field.Input
                                        placeholder="Naam"
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Description"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.description,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Omschrijving" />
                                    <field.Input
                                        placeholder="Omschrijving"
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="ElectricalOrganizationKinds"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.electricalOrganizationKinds,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Type organisatie" />
                                    <field.Popover>
                                        <field.CheckboxGroup options={[{ label: 'Inspectie', value: 'Inspection' }, { label: 'Geen', value: 'None' }]}>
                                            <field.Popover.Trigger asChild>
                                                <field.CheckboxGroup.Trigger placeholder="Select options" />
                                            </field.Popover.Trigger>
                                            <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                <field.CheckboxGroup options={[{ label: 'Inspectie', value: 'Inspection' }, { label: 'Geen', value: 'None' }]} />
                                            </field.Popover.Content>
                                        </field.CheckboxGroup>
                                    </field.Popover>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="NLKvK.KVKNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.createOrganizationCommandNLKvK.kVKNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="KVK nummer" />
                                    <field.Input
                                        placeholder="KVK nummer"
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Slogan"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.slogan,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Slogan" />
                                    <field.Input
                                        placeholder="Slogan"
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Email"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.email,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Email" />
                                    <field.Input
                                        placeholder="Email"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="TelephoneNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.telephoneNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Telefoonnummer" />
                                    <field.Input
                                        placeholder="Telefoonnummer"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="SciosRegistrationNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.sciosRegistrationNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="SCIOS" />
                                    <field.Input
                                        placeholder="SCIOS nummer"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />
                        <form.AppField
                            name="Website"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.website,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Website" />
                                    <field.Input
                                        placeholder="Website"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="IBAN"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.iBAN,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="IBAN" />
                                    <field.Input
                                        placeholder="IBAN"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="BIC"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.bIC,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="BIC" />
                                    <field.Input
                                        placeholder="BIC"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="VatNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.vatNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="BTW nummer" />
                                    <field.Input
                                        placeholder="BTW nummer"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />
                        <form.AppField
                            name="EnableElectricalProjectPlanning"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.enableElectricalProjectPlanning,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Planning module" />
                                    <field.Switch />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />
                        <form.AppField
                            name="EnableElectricalProjectPriceQuote"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.enableElectricalProjectPriceQuote,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Offerte module" />
                                    <field.Switch />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />
                        <form.AppField
                            name="EnableElectricalProjectRecommendations"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.enableElectricalProjectRecommendations,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Aanbevelingen op constateringen" />
                                    <field.Switch />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                    </Popout.Body>
                    <Popout.Footer className="flex flex-col gap-4">
                        <div className="w-full flex flex-row gap-2">
                            <Button className="w-full" variant="outline" onClick={handleClose} disabled={createOrganizationMutation.isPending}>Annuleren</Button>
                            <Button type="submit" className="w-full" disabled={createOrganizationMutation.isPending}>Opslaan</Button>
                        </div>
                        <em>
                            {createOrganizationMutation.isPending
                                ? 'Bezig met opslaan...'
                                : createOrganizationMutation.isError
                                    ? `Er is een fout opgetreden: ${createOrganizationMutation.error.Message}`
                                    : null}
                        </em>
                    </Popout.Footer>
                </form>
            </PopoutContent>
        </Popout>
    );
}

type FormValuesEdit = components['schemas']['EditOrganizationCommand'] & {
    File: File | null;
};

function EditOrganizationModal({
    isOpen,
    setIsOpen,
    organisationId,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    organisationId: string;
}) {
    const org = useQuery({
        ...portaalQueryOptions.organizations.view({
            orgainsationId: organisationId,
        }),
        enabled: isOpen && !!organisationId,
    });

    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const updateOrganizationMutation = api.useMutation(
        'put',
        '/api/v2/organizations/edit',
        {
            onSuccess: () => {
                toast.success('Organisatie is bewerkt');
            },
            onError: (error) => {
                logger.error({
                    system: 'EditOrganization',
                    error,
                }, { apiHttpMethod: 'Put', apiUrl: '/api/v2/organizations/edit' });

                toast.error('Er is een fout opgetreden');
            },
            onSettled: () => {
                queryClient.invalidateQueries(portaalQueryOptions.organizations.view({
                    orgainsationId: organisationId,
                }));
            },
        },
    );

    const getLogo = useQuery({
        ...portaalQueryOptions.organizations.logo({
            ImageQuality: 'OriginalWebp',
            orgainsationId: organisationId,
        }),
        enabled: isOpen && !!organisationId,
    });

    const updateLogo = api.useMutation('post', '/api/v2/organizations/files/logos/create');

    useEffect(() => {
        if (!isOpen)
            return;

        if (getLogo.isFetched && getLogo.isSuccess && getLogo.data) {
            const file = new File([getLogo.data], 'logo.webp', { type: getLogo.data.type || 'image/webp' });
            // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
            setSelectedFile(file);
        }
        else if (getLogo.isFetched && !getLogo.data) {
            // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
            setSelectedFile(null);
        }
    }, [getLogo.data, getLogo.isFetched, getLogo.isSuccess, organisationId, isOpen]);

    const defaultValues: FormValuesEdit = {
        Name: '',
        OrganizationId: organisationId,
        Description: null,
        Slogan: null,
        Email: null,
        ElectricalOrganizationKinds: [],
        TelephoneNumber: null,
        Website: null,
        IBAN: null,
        BIC: null,
        VatNumber: null,
        SciosRegistrationNumber: null,
        EnableElectricalProjectRecommendations: false,
        EnableElectricalProjectPlanning: false,
        EnableElectricalProjectPriceQuote: false,
        File: null,
    };

    const form = useAppForm({
        defaultValues,
        onSubmit: async ({ value }) => {
            await updateOrganizationMutation.mutateAsync({
                body: value,
            });
            setIsOpen(false);
            form.reset();

            if (value.File) {
                await updateLogo.mutateAsync({
                    params: {
                        query: {
                            OrganizationId: value.OrganizationId,
                        },
                    },
                    body: {
                        File: '',
                    },
                    bodySerializer: () => {
                        const formData = new FormData();
                        formData.append('File', selectedFile as Blob);
                        return formData;
                    },
                });
            }
        },
    });

    useEffect(() => {
        if (isOpen && org.isSuccess && org.data?.Organization) {
            form.reset({
                Name: org.data.Organization.Name ?? '',
                OrganizationId: organisationId,
                Description: org.data.Organization.Description ?? null,
                Slogan: org.data.Organization.Slogan ?? null,
                Email: org.data.Organization.Email ?? null,
                ElectricalOrganizationKinds: org.data.Organization.ElectricalOrganizationKinds ?? [],
                TelephoneNumber: org.data.Organization.TelephoneNumber ?? null,
                Website: org.data.Organization.Website ?? null,
                IBAN: org.data.Organization.IBAN ?? null,
                BIC: org.data.Organization.BIC ?? null,
                VatNumber: org.data.Organization.VatNumber ?? null,
                SciosRegistrationNumber: org.data.Organization.SciosRegistrationNumber ?? null,
                EnableElectricalProjectRecommendations: org.data.Organization.EnableElectricalProjectRecommendations ?? false,
                EnableElectricalProjectPlanning: org.data.Organization.EnableElectricalProjectPlanning ?? false,
                EnableElectricalProjectPriceQuote: org.data.Organization.EnableElectricalProjectPriceQuote ?? false,
                File: selectedFile,
            });
        }
    }, [org.isSuccess, org.data, organisationId, selectedFile, form, isOpen]);

    async function handleClose() {
        if (form.state.isTouched) {
            if (await showConfirm('Weet je zeker dat je wilt sluiten?')) {
                setIsOpen(false);
                setSelectedFile(null);
                form.reset();
                updateOrganizationMutation.reset();
            }
        }
        else {
            setIsOpen(false);
            setSelectedFile(null);
            form.reset();
            updateOrganizationMutation.reset();
        }
    }

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent asChild>
                <form onSubmit={onSubmit}>
                    <PopoutHeader>
                        <PopoutTitle>Organisatie bewerken</PopoutTitle>
                        <PopoutDescription children={<></>}></PopoutDescription>
                    </PopoutHeader>
                    <Popout.Body className="flex flex-col gap-4 py-6">
                        {getLogo.isFetched && (
                            <form.AppField
                                name="File"
                                children={field => (
                                    <field.Label>
                                        <field.Label.Title label="Logo" />
                                        <div>
                                            {!selectedFile && (
                                                <FileInput
                                                    onChange={(files) => {
                                                        setSelectedFile(files[0]);
                                                        form.setFieldValue('File', files[0]);
                                                    }}
                                                    accept="image/jpeg, image/png, image/webp, image/bmp, image/svg+xml"
                                                />
                                            )}
                                            {selectedFile && (
                                                <div className="flex justify-center relative">
                                                    <img
                                                        className="max-h-32"
                                                        src={URL.createObjectURL(selectedFile)}
                                                        alt="signature"
                                                    />
                                                    <Button
                                                        variant="ghost"
                                                        className="absolute right-0 top-1"
                                                        onClick={() => {
                                                            setSelectedFile(null);
                                                            form.setFieldValue('File', null);
                                                        }}
                                                    >
                                                        <Trash2 size={22} />
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            />
                        )}

                        <form.AppField
                            name="Name"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.name,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Naam" />
                                    <field.Input
                                        placeholder="Naam"
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Description"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.description,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Omschrijving" />
                                    <field.Input
                                        placeholder="Omschrijving"
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="ElectricalOrganizationKinds"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.electricalOrganizationKinds,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Type organisatie" />
                                    <field.Popover>
                                        <field.CheckboxGroup options={[{ label: 'Inspectie', value: 'Inspection' }, { label: 'Geen', value: 'None' }]}>
                                            <field.Popover.Trigger asChild>
                                                <field.CheckboxGroup.Trigger placeholder="Select options" />
                                            </field.Popover.Trigger>
                                            <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                <field.CheckboxGroup options={[{ label: 'Inspectie', value: 'Inspection' }, { label: 'Geen', value: 'None' }]} />
                                            </field.Popover.Content>
                                        </field.CheckboxGroup>
                                    </field.Popover>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Slogan"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.slogan,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Slogan" />
                                    <field.Input
                                        placeholder="Slogan"
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />

                        <form.AppField
                            name="Email"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.email,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Email" />
                                    <field.Input
                                        placeholder="Email"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="TelephoneNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.telephoneNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Telefoonnummer" />
                                    <field.Input
                                        placeholder="Telefoonnummer"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="Website"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.website,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Website" />
                                    <field.Input
                                        placeholder="Website"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="IBAN"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.iBAN,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="IBAN" />
                                    <field.Input
                                        placeholder="IBAN"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="BIC"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.bIC,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="BIC" />
                                    <field.Input
                                        placeholder="BIC"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="SciosRegistrationNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.sciosRegistrationNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="SCIOS" />
                                    <field.Input
                                        placeholder="SCIOS nummer"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />

                        <form.AppField
                            name="VatNumber"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.vatNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="BTW nummer" />
                                    <field.Input
                                        placeholder="BTW nummer"
                                    />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />
                        <form.AppField
                            name="EnableElectricalProjectPlanning"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.enableElectricalProjectPlanning,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Planning module" />
                                    <field.Switch />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />
                        <form.AppField
                            name="EnableElectricalProjectPriceQuote"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.enableElectricalProjectPriceQuote,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Offerte module" />
                                    <field.Switch />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />
                        <form.AppField
                            name="EnableElectricalProjectRecommendations"
                            validators={{
                                onChange:
                                    validators.createOrganizationCommand.enableElectricalProjectRecommendations,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label="Aanbevelingen op constateringen" />
                                    <field.Switch />
                                    <field.ValidationError />
                                </field.Label>

                            )}
                        />
                    </Popout.Body>
                    <Popout.Footer className="flex flex-col gap-4">
                        <div className="w-full flex flex-row gap-2">
                            <Button className="w-full" variant="outline" onClick={handleClose} disabled={updateOrganizationMutation.isPending}>Annuleren</Button>
                            <Button type="submit" className="w-full" disabled={updateOrganizationMutation.isPending}>Opslaan</Button>
                        </div>
                        <em>
                            {updateOrganizationMutation.isPending
                                ? 'Bezig met opslaan...'
                                : updateOrganizationMutation.isError
                                    ? `Er is een fout opgetreden: ${updateOrganizationMutation.error.Message}`
                                    : null}
                        </em>
                    </Popout.Footer>
                </form>
            </PopoutContent>
        </Popout>
    );
}
